import { useCallback, useEffect, useState } from "react";

import type {
  CaseSummary,
  ConversationDetail,
  ConversationSummary,
  Locale,
  MeResponse,
} from "../../shared/types";
import * as api from "./lib/api";
import type { CaseDetail, ConfigResponse, DocumentSummary, Respondent } from "./lib/api";
import { ApiError } from "./lib/api";
import { COPY, useCopy } from "./lib/copy";
import Auth, { type AuthFailure } from "./components/Auth";
import { Welcome } from "./components/Landing";
import Stage from "./components/Stage";
import Studies from "./components/Studies";

export type Theme = "dark" | "light";

/** What the stage is being told, and how loudly. There are no toasts in this product. */
export interface StageNotice {
  text: string;
  tone: "quiet" | "warn" | "error";
}

/**
 * How long anything the page is told from OUTSIDE a turn holds the status line.
 *
 * There is one status line and it is the only channel this product has for live
 * state. A notice with no expiry occupies it forever: after one failed rename the
 * founder sent messages, got replies, and the line went on saying "The name did not
 * save." — LISTENING, THINKING and SPEAKING were never rendered again for the rest
 * of the session. The Stage's own notices already expire (4s for recovery, 6s for a
 * finished intake); these are the same kind of thing and now do too.
 */
const NOTICE_MS = 6000;

const THEME_KEY = "custdev.theme";
const CASE_KEY = "custdev.case";
/** Set once anyone has signed in here, so a first visit can be told from a return. */
const SEEN_KEY = "custdev.seen";
/**
 * Per account, set once the framing screen has been read or skipped.
 *
 * It is a second lock rather than the only one. The first is the server's own
 * account of the account — no conversation and no findings — which is what makes
 * the screen appear for a genuinely new account on any device and for nobody
 * else. This one covers the gap that leaves: a reload in the thirty seconds
 * between reading the screen and saying the first word.
 */
function onboardedKey(userId: number): string {
  return `custdev.onboarded.${userId}`;
}

function hasOnboarded(userId: number): boolean {
  try {
    return window.localStorage.getItem(onboardedKey(userId)) === "1";
  } catch {
    // A browser that will not remember is a browser that sees one extra screen
    // with a way past it. That is a fine failure.
    return false;
  }
}

/**
 * Brand new, as the server sees it: an account that has never held a conversation
 * and has nothing written down.
 *
 * Read from the identity rather than from the conversation on screen, and read
 * ONCE, at the moment the session is established. Both matter. The conversation
 * is created by the client a beat later, so a check against it would race itself;
 * and a person who starts a second conversation in an empty case is not new and
 * must not be walled again.
 */
function isNewAccount(identity: MeResponse): boolean {
  return identity.latestConversationId === null && identity.findingCount === 0;
}

/** True until somebody has signed in from this browser at least once. */
function firstVisit(): boolean {
  try {
    return window.localStorage.getItem(SEEN_KEY) !== "1";
  } catch {
    return false;
  }
}

/** The browser chrome above a paper-white page must not stay near-black. */
const THEME_META: Record<Theme, string> = { dark: "#0e0c0a", light: "#faf9f7" };

function readTheme(): Theme {
  try {
    return window.localStorage.getItem(THEME_KEY) === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

/**
 * The language to open in before the server has said anything. A Russian founder
 * registering in English wrote `en` onto their account and then read English replies
 * for the rest of the session; the browser already knows better than a hard-coded
 * default does.
 */
function readLocale(): Locale {
  const languages = typeof navigator === "undefined" ? [] : navigator.languages ?? [navigator.language];
  return languages.some((tag) => typeof tag === "string" && tag.toLowerCase().startsWith("ru"))
    ? "ru"
    : "en";
}

/** Which business was on the table when this browser was last closed. */
function readCase(): number | null {
  try {
    const raw = window.localStorage.getItem(CASE_KEY);
    if (raw === null) return null;
    const id = Number.parseInt(raw, 10);
    return Number.isSafeInteger(id) ? id : null;
  } catch {
    return null;
  }
}

/**
 * A remembered case only counts if it still exists — the consultant may have signed in
 * as someone else, or archived it from another browser. The server's own idea of the
 * active case is the fallback, and the newest case is the fallback for that.
 */
function chooseCase(cases: CaseSummary[], remembered: number | null, server: number): number | null {
  const known = (id: number | null): boolean =>
    id !== null && cases.some((item) => item.id === id);
  if (known(remembered)) return remembered;
  if (known(server)) return server;
  return cases[0]?.id ?? null;
}

/** Newest first, so the case opens where the founder stopped talking. */
function newestFirst(a: ConversationSummary, b: ConversationSummary): number {
  return Date.parse(b.updatedAt) - Date.parse(a.updatedAt);
}

/**
 * A 4xx carries a message this product wrote, so it is shown as-is. A 5xx or a
 * transport failure carries whatever a proxy felt like saying — "Internal Server
 * Error" is not something the consultant would ever come out with — so it is replaced.
 */
function describe(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.status >= 400 && err.status < 500
    ? err.message
    : fallback;
}

/**
 * The same judgement, keeping the code. The auth form needs it: a message alone
 * cannot say which field was refused, and marking both fields on every failure
 * tells somebody their correct address is also a mistake.
 */
function failure(err: unknown, fallback: string): AuthFailure {
  return {
    message: describe(err, fallback),
    code: err instanceof ApiError ? err.code : null,
  };
}

export default function App() {
  const [theme, setTheme] = useState<Theme>(readTheme);
  const [locale, setLocale] = useState<Locale>(readLocale);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [cases, setCases] = useState<CaseSummary[]>([]);
  /** Every conversation the consultant owns; `Cases` shows the ones in the open case. */
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeCaseId, setActiveCaseId] = useState<number | null>(null);
  const [conversation, setConversation] = useState<ConversationDetail | null>(null);
  const [booted, setBooted] = useState(false);
  const [busy, setBusy] = useState(true);
  const [authError, setAuthError] = useState<AuthFailure | null>(null);
  /**
   * The one honest moment before the intake starts, for an account that has never
   * had one. Set where the identity is settled — boot and sign-in — and never
   * from inside the conversation, so nothing that happens during a session can
   * put this screen back in front of somebody.
   */
  const [welcome, setWelcome] = useState(false);
  const [stageNotice, setStageNotice] = useState<StageNotice | null>(null);
  /** From GET /api/config. Null until it answers; unreachable reads as open. */
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  /** What the consultant knows about the open case: respondent, research, counts. */
  const [caseDetail, setCaseDetail] = useState<CaseDetail | null>(null);
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  /** A file is on its way up and being read. The blob thinks for exactly this long. */
  const [uploading, setUploading] = useState(false);
  /**
   * The name of the business being opened, while it is opening. The switcher commits
   * to the new name immediately — that is deliberate — so everything under it has to
   * be told to wait, or the founder reads one business's name over another's notes.
   */
  const [opening, setOpening] = useState<string | null>(null);
  /**
   * The studies surface, and which study is open on it.
   *
   * Studies live ALONGSIDE the conversation inside a case, not instead of it. This is
   * a view switch and nothing else: the case, the dossier and the transcript are
   * exactly where they were when you come back, and the conversation is re-read from
   * the server on the way out so anything said before you left is still on screen.
   */
  const [inStudies, setInStudies] = useState(false);
  const [studyId, setStudyId] = useState<number | null>(null);

  const copy = useCopy(locale);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_META[theme]);
    try {
      window.localStorage.setItem(THEME_KEY, theme);
    } catch {
      // A theme that cannot be remembered is still a theme that works this session.
    }
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  /**
   * Every notice is transient. Keyed on the notice object, so a second failure
   * restarts the clock rather than inheriting the first one's remaining time, and
   * the timer is cleared by anything that replaces or drops the notice first.
   */
  useEffect(() => {
    if (stageNotice === null) return;
    const timer = window.setTimeout(() => setStageNotice(null), NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [stageNotice]);

  /** Pressing Return answers the complaint; the status line goes back to the turn. */
  const dismissNotice = useCallback((): void => setStageNotice(null), []);

  // Whether sign-up exists at all is the server's call, made before anyone signs in.
  // Unreachable config behaves as today's open registration rather than a locked door.
  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        setConfig(await api.getConfig(controller.signal));
      } catch {
        // Null stays; the landing reads null as open.
      }
    })();
    return () => controller.abort();
  }, []);

  // What the consultant knows about the case on the table. Peripheral on purpose:
  // a page that cannot read the respondent or the documents still holds the
  // conversation, so failures here set nothing and say nothing.
  const signedIn = me !== null;
  useEffect(() => {
    setCaseDetail(null);
    setDocuments([]);
    if (!signedIn || activeCaseId === null) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const detail = await api.getCase(activeCaseId, controller.signal);
        if (!controller.signal.aborted) setCaseDetail(detail);
      } catch {
        // The menu simply shows no respondent line.
      }
    })();
    void (async () => {
      try {
        const docs = await api.listDocuments(activeCaseId, controller.signal);
        if (!controller.signal.aborted) setDocuments(docs);
      } catch {
        // The rail simply lists no documents.
      }
    })();
    return () => controller.abort();
  }, [signedIn, activeCaseId]);

  // A reload lands on the business you were last advising, not on whichever one the
  // server happens to think is active.
  useEffect(() => {
    if (activeCaseId === null) return;
    try {
      window.localStorage.setItem(CASE_KEY, String(activeCaseId));
    } catch {
      // A case that cannot be remembered still works for this session.
    }
  }, [activeCaseId]);

  // A study belongs to a case, so changing the business on the table cannot leave
  // one case's study open under another case's name.
  useEffect(() => {
    setStudyId(null);
  }, [activeCaseId]);

  /** Keeps the switcher's list of sessions honest without a second round trip. */
  const refreshConversations = useCallback((): void => {
    void (async () => {
      try {
        setConversations(await api.listConversations());
      } catch {
        // The list is orientation, not the conversation. An old one is not a failure.
      }
    })();
  }, []);

  /**
   * The conversation a case opens into: the one it was left in, or a fresh one. A
   * conversation that will not load must not lock anyone out of the case, so a failed
   * read falls through to starting a new one and only then admits defeat.
   *
   * `known` lets the caller hand over a list it already has in flight, which is what
   * keeps boot from walking the same request twice in a row.
   */
  const loadCase = useCallback(
    async (
      caseId: number | null,
      language: Locale,
      known?: ConversationSummary[],
    ): Promise<ConversationDetail> => {
      let all = known;
      if (all === undefined) {
        try {
          all = await api.listConversations();
        } catch {
          all = [];
        }
      }
      setConversations(all);
      // A null case id means we could not read the case list at all; the server then
      // scopes the new conversation to whichever case it considers active.
      const mine = caseId === null ? all : all.filter((item) => item.caseId === caseId);
      const newest = mine.slice().sort(newestFirst)[0];
      if (newest !== undefined) {
        try {
          return await api.getConversation(newest.id);
        } catch {
          // Falls through rather than leaving the case unopenable.
        }
      }
      const created = await api.createConversation({ caseId: caseId ?? undefined, language });
      refreshConversations();
      return created;
    },
    [refreshConversations],
  );

  const openCase = useCallback(
    async (
      caseId: number | null,
      language: Locale,
      known?: ConversationSummary[],
    ): Promise<void> => {
      try {
        setConversation(await loadCase(caseId, language, known));
        setStageNotice(null);
      } catch (err) {
        setConversation(null);
        setStageNotice({ text: describe(err, copy.errorNetwork), tone: "error" });
      }
    },
    [copy.errorNetwork, loadCase],
  );

  /**
   * Everything that has to happen once an identity is in hand, on boot and after auth.
   *
   * The two lists are asked for together. They were sequential, which put four strictly
   * serial round trips between opening the page and the first word of the conversation.
   */
  const enter = useCallback(
    async (identity: MeResponse): Promise<void> => {
      // Decided here, before anything is loaded, because this is the only place
      // that holds the server's own account of the account. Everything below
      // creates a case and a conversation, at which point "has never had one" is
      // no longer answerable.
      setWelcome(isNewAccount(identity) && !hasOnboarded(identity.user.id));

      const [caseResult, conversationResult] = await Promise.allSettled([
        api.listCases(),
        api.listConversations(),
      ]);
      // The switcher can be empty. The conversation still has to open.
      let list: CaseSummary[] = caseResult.status === "fulfilled" ? caseResult.value : [];
      const all =
        conversationResult.status === "fulfilled" ? conversationResult.value : undefined;

      let id = chooseCase(list, readCase(), identity.activeCaseId);
      if (id === null && list.length === 0) {
        try {
          const created = await api.createCase();
          list = [created];
          id = created.id;
        } catch {
          // The server will scope to a case of its own; the switcher has nothing to show.
        }
      }
      setCases(list);
      setActiveCaseId(id);
      await openCase(id, identity.user.locale, all);
    },
    [openCase],
  );

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const identity = await api.getMe();
        if (!alive) return;
        setMe(identity);
        setLocale(identity.user.locale);
        await enter(identity);
      } catch (err) {
        // 401 is the ordinary signed-out case, not a failure worth a message.
        if (alive && !(err instanceof ApiError && err.status === 401)) {
          setAuthError(failure(err, copy.errorNetwork));
        }
      } finally {
        if (alive) {
          setBusy(false);
          setBooted(true);
        }
      }
    })();
    return () => {
      alive = false;
    };
    // Runs once: the session is read at startup and afterwards only auth actions change it.
  }, []);

  const submitAuth = useCallback(
    (mode: "signIn" | "signUp", email: string, password: string): void => {
      setBusy(true);
      setAuthError(null);
      void (async () => {
        try {
          const identity =
            mode === "signIn"
              ? await api.login({ email, password })
              : await api.register({ email, password, locale });
          setMe(identity);
          setLocale(identity.user.locale);
          try {
            window.localStorage.setItem(SEEN_KEY, "1");
          } catch {
            // The next visit simply opens on sign-up again. Nothing is lost.
          }
          await enter(identity);
        } catch (err) {
          setAuthError(failure(err, copy.errorGeneric));
        } finally {
          setBusy(false);
        }
      })();
    },
    [copy.errorGeneric, enter, locale],
  );

  const signOut = useCallback((): void => {
    void (async () => {
      try {
        await api.logout();
      } catch {
        // The cookie is either already gone or will be rejected on the next call.
      }
      setMe(null);
      setCases([]);
      setConversations([]);
      setActiveCaseId(null);
      setConversation(null);
      setStageNotice(null);
      setAuthError(null);
      setWelcome(false);
      try {
        // The next person at this browser is not necessarily the same consultant.
        window.localStorage.removeItem(CASE_KEY);
      } catch {
        // A stored id that outlives the session is checked against the case list anyway.
      }
    })();
  }, []);

  const nameOf = useCallback(
    (id: number | null): string => {
      const found = cases.find((item) => item.id === id);
      const name = (found?.name ?? "").trim();
      return name.length > 0 ? name : copy.caseUntitled;
    },
    [cases, copy.caseUntitled],
  );

  /**
   * Switching is committed to the trigger before the conversation has loaded, so the
   * name and the transcript can disagree for as long as the request takes. Two things
   * follow. A failure has to put the old case back: a switcher showing one business
   * over another business's conversation is the exact bug this whole feature exists to
   * kill. And for the second the load takes, `opening` blanks everything under the
   * name rather than leaving the previous client's transcript and notes sitting there.
   */
  const switchCase = useCallback(
    (id: number): void => {
      if (id === activeCaseId) return;
      const previous = activeCaseId;
      setActiveCaseId(id);
      setStageNotice(null);
      setOpening(nameOf(id));
      void (async () => {
        try {
          setConversation(await loadCase(id, locale));
        } catch (err) {
          setActiveCaseId(previous);
          setStageNotice({ text: describe(err, copy.caseSwitchFailed), tone: "error" });
        } finally {
          setOpening(null);
        }
      })();
    },
    [activeCaseId, copy.caseSwitchFailed, loadCase, locale, nameOf],
  );

  const createCase = useCallback(
    (name: string): void => {
      void (async () => {
        let created: CaseSummary;
        try {
          created = await api.createCase(name.trim().length === 0 ? undefined : name.trim());
        } catch (err) {
          setStageNotice({ text: describe(err, copy.caseCreateFailed), tone: "error" });
          return;
        }
        setCases((current) => [created, ...current]);
        setActiveCaseId(created.id);
        setStageNotice(null);
        setOpening((created.name ?? "").trim().length > 0 ? created.name : copy.caseUntitled);
        try {
          setConversation(await api.createConversation({ caseId: created.id, language: locale }));
          refreshConversations();
        } catch (err) {
          setConversation(null);
          setStageNotice({ text: describe(err, copy.errorNetwork), tone: "error" });
        } finally {
          setOpening(null);
        }
      })();
    },
    [copy.caseCreateFailed, copy.caseUntitled, copy.errorNetwork, locale, refreshConversations],
  );

  /** Applied on the spot and put back if the server disagrees. A name is not worth a wait. */
  const renameCase = useCallback(
    (id: number, name: string): void => {
      const previous = cases;
      setCases((current) =>
        current.map((item) => (item.id === id ? { ...item, name } : item)),
      );
      void (async () => {
        try {
          const saved = await api.updateCase(id, { name });
          setCases((current) => current.map((item) => (item.id === id ? saved : item)));
        } catch (err) {
          setCases(previous);
          setStageNotice({ text: describe(err, copy.caseRenameFailed), tone: "error" });
        }
      })();
    },
    [cases, copy.caseRenameFailed],
  );

  /** Reopening a session the founder had already had. Same case, older transcript. */
  const openConversation = useCallback(
    (id: number): void => {
      if (conversation !== null && conversation.id === id) return;
      setStageNotice(null);
      void (async () => {
        try {
          setConversation(await api.getConversation(id));
        } catch (err) {
          setStageNotice({ text: describe(err, copy.conversationOpenFailed), tone: "error" });
        }
      })();
    },
    [conversation, copy.conversationOpenFailed],
  );

  /**
   * A fresh conversation inside the case that is already open. The dossier is the
   * case's, so nothing is forgotten — which is exactly what `sessionNew` says.
   */
  const startConversation = useCallback(
    (language?: Locale): void => {
      void (async () => {
        try {
          const created = await api.createConversation({
            caseId: activeCaseId ?? undefined,
            language: language ?? locale,
          });
          setConversation(created);
          setStageNotice({ text: copy.sessionNew, tone: "quiet" });
          refreshConversations();
        } catch (err) {
          setStageNotice({ text: describe(err, copy.errorNetwork), tone: "error" });
        }
      })();
    },
    [activeCaseId, copy.errorNetwork, copy.sessionNew, locale, refreshConversations],
  );

  const removeConversation = useCallback(
    (id: number): void => {
      void (async () => {
        try {
          await api.deleteConversation(id);
        } catch (err) {
          setStageNotice({ text: describe(err, copy.deleteFailed), tone: "error" });
          return;
        }
        const remaining = conversations.filter((item) => item.id !== id);
        setConversations(remaining);
        setStageNotice(null);
        // Deleting the conversation on screen has to leave something on screen.
        if (conversation !== null && conversation.id === id) {
          await openCase(activeCaseId, locale, remaining);
        }
      })();
    },
    [activeCaseId, conversation, conversations, copy.deleteFailed, locale, openCase],
  );

  /**
   * The intake finished mid-conversation: the server has already written the
   * respondent onto the case, so the client's copy just catches up. No navigation —
   * the same conversation goes on as a session.
   */
  const intakeComplete = useCallback((respondent: Respondent): void => {
    setCaseDetail((current) =>
      current === null
        ? current
        : { ...current, respondent, intakeCompletedAt: new Date().toISOString() },
    );
  }, []);

  /**
   * A fresh intake for the open case. The old respondent stays in force until the
   * new conversation completes and replaces it, which is the server's rule too.
   */
  const redoIntake = useCallback((): void => {
    if (activeCaseId === null) return;
    void (async () => {
      try {
        const created = await api.redoIntake(activeCaseId);
        setConversation(created);
        setStageNotice(null);
        refreshConversations();
      } catch (err) {
        setStageNotice({ text: describe(err, copy.errorNetwork), tone: "error" });
      }
    })();
  }, [activeCaseId, copy.errorNetwork, refreshConversations]);

  /**
   * The upload waits for the server to read and summarise the file, so the blob
   * thinks for the few seconds it takes — uploading is a deliberate act.
   */
  const uploadDocument = useCallback(
    (file: File): void => {
      if (activeCaseId === null) return;
      setUploading(true);
      void (async () => {
        try {
          const doc = await api.uploadDocument(activeCaseId, file);
          setDocuments((current) => [doc, ...current.filter((item) => item.id !== doc.id)]);
          setStageNotice(null);
        } catch (err) {
          setStageNotice({ text: describe(err, copy.documentUploadFailed), tone: "error" });
        } finally {
          setUploading(false);
        }
      })();
    },
    [activeCaseId, copy.documentUploadFailed],
  );

  const removeDocument = useCallback(
    (id: number): void => {
      void (async () => {
        try {
          await api.deleteDocument(id);
          setDocuments((current) => current.filter((item) => item.id !== id));
        } catch (err) {
          setStageNotice({ text: describe(err, copy.deleteFailed), tone: "error" });
        }
      })();
    },
    [copy.deleteFailed],
  );

  /**
   * The chrome's language and the conversation's are two different things, and until
   * this existed only the first of them moved: a founder pressed `Русский`, read a
   * Russian interface and went on getting English replies in an English voice.
   *
   * A conversation nobody has spoken into yet is simply reopened in the new language.
   * One that is under way is not rewritten — the transcript is in the language it was
   * said in — so the founder is told, and offered the new conversation instead.
   */
  const changeLocale = useCallback(
    (next: Locale): void => {
      setLocale(next);
      void api.updateMe({ locale: next }).then(setMe).catch(() => {
        // The interface is already in the new language; the server catches up next time.
      });
      if (conversation === null || conversation.language === next) return;
      if (conversation.messages.some((message) => message.role === "user")) {
        // In the language they just asked for: it is the answer to what they pressed.
        setStageNotice({ text: COPY[next].languageLocked, tone: "quiet" });
        return;
      }
      startConversation(next);
    },
    [conversation, startConversation],
  );

  const openStudies = useCallback((): void => {
    setStudyId(null);
    setInStudies(true);
  }, []);

  /**
   * Back to the conversation. The transcript is re-read first, because everything
   * said in this session lives in the Stage's own turn state and the Stage was
   * unmounted while the studies were on screen. Without this, coming back would show
   * the conversation as the server last described it and silently drop the last ten
   * minutes of it. A failed read is not worth blocking on: the snapshot we already
   * hold is still this conversation.
   */
  const leaveStudies = useCallback((): void => {
    const id = conversation === null ? null : conversation.id;
    if (id === null) {
      setInStudies(false);
      return;
    }
    void (async () => {
      try {
        setConversation(await api.getConversation(id));
      } catch {
        // Whatever we already have is still the conversation.
      }
      setInStudies(false);
    })();
  }, [conversation]);

  /** Anything that navigates to a conversation leaves the studies behind it. */
  const openConversationHere = useCallback(
    (id: number): void => {
      setInStudies(false);
      openConversation(id);
    },
    [openConversation],
  );

  const newConversationHere = useCallback((): void => {
    setInStudies(false);
    startConversation();
  }, [startConversation]);

  const redoIntakeHere = useCallback((): void => {
    setInStudies(false);
    redoIntake();
  }, [redoIntake]);

  const toggleTheme = useCallback((): void => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  /**
   * Read, or skipped. There is deliberately no difference between the two: both
   * lead to the same conversation, and both mean this screen is done. A "skip"
   * that costs you something is not a skip.
   */
  const leaveWelcome = useCallback((): void => {
    setWelcome(false);
    if (me === null) return;
    try {
      window.localStorage.setItem(onboardedKey(me.user.id), "1");
    } catch {
      // Unremembered is survivable: the server-side condition below still stops
      // this appearing once there is a conversation to come back to.
    }
  }, [me]);

  if (!me) {
    return (
      <Auth
        locale={locale}
        phase={!booted ? "booting" : busy ? "submitting" : "ready"}
        error={authError}
        firstVisit={firstVisit()}
        // An unreachable /api/config used to read as open registration, which put
        // a create-account form in front of somebody the server would then refuse.
        // Unknown now reads as closed: the worst case is an invited person seeing
        // one sign-in form, which is the form they wanted.
        access={config?.access ?? "private"}
        onSubmit={submitAuth}
        onChangeLocale={setLocale}
      />
    );
  }

  // Between the account existing and the interview starting. One screen, and the
  // only thing in this product that stands between somebody and their
  // conversation — which is why it is skippable and why it never comes back.
  if (welcome) {
    return (
      <Welcome
        locale={locale}
        onStart={leaveWelcome}
        onSkip={leaveWelcome}
        onChangeLocale={changeLocale}
      />
    );
  }

  // The other half of the product. It shares the top bar, the case switcher, the
  // blob and the one status line with the conversation, and nothing else: the stage,
  // the dock and the notes rail are not rendered here and not touched by it.
  if (inStudies && activeCaseId !== null) {
    return (
      <Studies
        locale={locale}
        theme={theme}
        caseId={activeCaseId}
        cases={cases}
        conversations={conversations}
        activeConversationId={conversation === null ? null : conversation.id}
        respondent={caseDetail?.respondent ?? null}
        studyId={studyId}
        onOpenStudy={setStudyId}
        onBack={leaveStudies}
        onSwitchCase={switchCase}
        onCreateCase={createCase}
        onRenameCase={renameCase}
        onOpenConversation={openConversationHere}
        onNewConversation={newConversationHere}
        onDeleteConversation={removeConversation}
        onRedoIntake={redoIntakeHere}
        onUploadDocument={uploadDocument}
        onToggleTheme={toggleTheme}
        onChangeLocale={changeLocale}
        onSignOut={signOut}
      />
    );
  }

  return (
    <Stage
      key={conversation ? conversation.id : "none"}
      locale={locale}
      conversation={conversation}
      conversations={conversations}
      cases={cases}
      activeCaseId={activeCaseId}
      theme={theme}
      opening={opening}
      notice={stageNotice}
      onDismissNotice={dismissNotice}
      respondent={caseDetail?.respondent ?? null}
      documents={documents}
      uploading={uploading}
      onIntakeComplete={intakeComplete}
      onRedoIntake={redoIntake}
      onUploadDocument={uploadDocument}
      onDeleteDocument={removeDocument}
      onSwitchCase={switchCase}
      onCreateCase={createCase}
      onRenameCase={renameCase}
      onOpenConversation={openConversation}
      onNewConversation={startConversation}
      onDeleteConversation={removeConversation}
      onOpenStudies={openStudies}
      onToggleTheme={toggleTheme}
      onChangeLocale={changeLocale}
      onSignOut={signOut}
    />
  );
}
